function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { forwardRef } from 'react';
import { canHaveRef } from "./ComponentUtils.js";
import { useXComponent } from "./XComponentMap.js";
/**
 * Helper function that will wrap the provided component, and return an ExtendableComponent type.
 * Whenever that ExtendableComponent is used, it will check if there is a replacement component for the provided component on the context.
 * If there is, it will use that component instead of the provided component.
 * This is a similar concept to how swizzling is done in Docusaurus or obj-c, but for any React component.
 *
 * Usage:
 *
 * ```tsx
 * function MyComponent() {
 *   return <div>MyComponent</div>;
 * }
 *
 * const XMyComponent = extendableComponent(MyComponent);
 *
 * function MyReplacementComponent() {
 *  return <div>MyReplacementComponent</div>;
 * }
 *
 * <XMyComponent /> // Will render MyComponent
 *
 * <XComponentMapProvider value={new Map([[XMyComponent, MyReplacementComponent]])}>
 *   <XMyComponent /> // Will render MyReplacementComponent
 * </XComponentMapProvider>
 *
 * ```
 *
 * Is useful in cases where we have a component deep down in the component tree that we want to replace with a different component, but don't want to
 * have to provide props at the top level just to hook into that.
 *
 * @param Component The component to wrap
 * @returns The wrapped component
 */
import { jsx as _jsx } from "react/jsx-runtime";
export function createXComponent(Component) {
  var _Component$displayNam;
  var forwardedRefComponent;
  function XComponent(props, ref) {
    var ReplacementComponent = useXComponent(forwardedRefComponent);
    return canHaveRef(Component) ?
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    _jsx(ReplacementComponent, _objectSpread(_objectSpread({}, props), {}, {
      ref: ref
    })) :
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    _jsx(ReplacementComponent, _objectSpread({}, props));
  }

  // Add the display name so this appears as a tag in the React DevTools
  // Need to add it here, and then when it's wrapped with the `forwardRef` it will automatically get the display name of the original component
  XComponent.displayName = "XComponent(".concat((_Component$displayNam = Component.displayName) !== null && _Component$displayNam !== void 0 ? _Component$displayNam : Component.name, ")");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardedRefComponent = /*#__PURE__*/forwardRef(XComponent);
  forwardedRefComponent.Original = Component;
  forwardedRefComponent.isXComponent = true;
  return forwardedRefComponent;
}
export default createXComponent;
//# sourceMappingURL=XComponent.js.map